/** Read a numeric % from GET /api/settings; 0 is valid (do not treat as missing). */
export function parseIndiaSitePercent(settingsResponse, key, fallback) {
  if (!settingsResponse || typeof settingsResponse !== 'object') return fallback;
  if (!Object.prototype.hasOwnProperty.call(settingsResponse, key)) return fallback;
  const n = Number(settingsResponse[key]);
  return Number.isFinite(n) ? n : fallback;
}

/**
 * Resolved India discount rule before GST (member bands, then flat client %).
 * Band rules are exclusive: first matching [min,max] wins; use either % or fixed INR on that band.
 * When the client has no band match and no `india_discount_percent`, the effective discount is **0%**.
 * (Site “alt payment” % is not applied here — empty CRM discount means no extra India discount.)
 *
 * @param {object|null} clientPricing
 * @param {number} memberCount
 * @param {number} [_siteAltPercent] ignored; kept for call-site compatibility
 */
export function resolveIndiaDiscountRule(clientPricing, memberCount, _siteAltPercent) {
  const cp = clientPricing || {};
  const bands = cp.india_discount_member_bands;
  const n = Math.max(0, Math.floor(Number(memberCount)));
  if (Array.isArray(bands) && bands.length > 0) {
    for (const b of bands) {
      const lo = Math.max(0, Math.floor(Number(b.min)));
      const hi = b.max == null ? lo : Math.max(lo, Math.floor(Number(b.max)));
      if (n < lo || n > hi) continue;

      const amtRaw = b.amount ?? b.amount_inr;
      if (amtRaw != null && amtRaw !== '' && Number(amtRaw) > 0) {
        return {
          fromBand: true,
          mode: 'amount',
          amountInr: Number(amtRaw),
          percent: 0,
          label: 'Group discount',
        };
      }
      const p = b.percent;
      if (p != null && p !== '' && Number.isFinite(Number(p)) && Number(p) >= 0) {
        return {
          fromBand: true,
          mode: 'percent',
          amountInr: 0,
          percent: Number(p),
          label: 'Group discount',
        };
      }
    }
  }

  const rawDisc = cp.india_discount_percent;
  const hasClientDiscount = rawDisc != null && rawDisc !== '';
  if (hasClientDiscount) {
    return {
      fromBand: false,
      mode: 'percent',
      amountInr: 0,
      percent: Number(rawDisc) || 0,
      label: 'India discount',
    };
  }
  return {
    fromBand: false,
    mode: 'percent',
    amountInr: 0,
    percent: 0,
    label: 'No client discount',
  };
}

/**
 * @deprecated Use resolveIndiaDiscountRule — kept for narrow compatibility (percent-only).
 */
export function resolveIndiaDiscountPercent(clientPricing, memberCount, siteAltPercent) {
  const r = resolveIndiaDiscountRule(clientPricing, memberCount, siteAltPercent);
  const discountPct =
    r.mode === 'amount' && r.amountInr > 0
      ? 0
      : Number(r.percent) || 0;
  return { discountPct, fromBand: r.fromBand };
}

/**
 * Apply resolved rule to an INR base (after cart promos).
 */
export function applyIndiaDiscountRuleToBase(base, rule) {
  const b = Math.max(0, Number(base));
  if (!Number.isFinite(b) || b <= 0) {
    return {
      discountAmt: 0,
      discountKind: 'percent',
      discountNominalPercent: 0,
      discountPctEffective: 0,
    };
  }
  if (rule.mode === 'amount' && rule.amountInr > 0) {
    const discountAmt = Math.min(b, rule.amountInr);
    const discountPctEffective = b > 0 ? (discountAmt / b) * 100 : 0;
    return {
      discountAmt,
      discountKind: 'amount',
      discountNominalPercent: null,
      discountPctEffective,
    };
  }
  const pct = Number(rule.percent) || 0;
  const discountAmt = (b * pct) / 100;
  return {
    discountAmt,
    discountKind: 'percent',
    discountNominalPercent: pct,
    discountPctEffective: pct,
  };
}

/**
 * India manual / UPI checkout math — aligned with IndiaPaymentPage (discount on net after cart promos, then GST + platform on taxable base).
 * @param {number} effectiveBase - Amount after cart subtotal minus promo and automatic cart discounts (INR).
 * @param {object|null} clientPricing - From GET /api/student/home `client_india_pricing`.
 * @param {object} settings - Site settings: india_gst_percent, india_platform_charge_percent.
 * @param {number} [memberCount] - Total participants; when set, applies `india_discount_member_bands` if configured.
 * @returns {object|null} null if effectiveBase <= 0
 */
export function computeIndiaCheckoutBreakdown(effectiveBase, clientPricing, settings, memberCount) {
  const base = Number(effectiveBase);
  if (!Number.isFinite(base) || base <= 0) return null;

  const s = settings || {};
  const platformPctN = parseIndiaSitePercent(s, 'india_platform_charge_percent', 3);
  const siteGstN = parseIndiaSitePercent(s, 'india_gst_percent', 18);

  const cp = clientPricing || {};
  const mcRaw =
    memberCount != null && memberCount !== '' ? Number(memberCount) : NaN;

  let rule;
  if (Number.isFinite(mcRaw)) {
    rule = resolveIndiaDiscountRule(cp, mcRaw, 0);
  } else {
    const rawDisc = cp.india_discount_percent;
    const hasClientDiscount = rawDisc != null && rawDisc !== '';
    rule = hasClientDiscount
      ? {
          fromBand: false,
          mode: 'percent',
          amountInr: 0,
          percent: Number(rawDisc) || 0,
          label: 'India discount',
        }
      : {
          fromBand: false,
          mode: 'percent',
          amountInr: 0,
          percent: 0,
          label: 'No client discount',
        };
  }

  const applied = applyIndiaDiscountRuleToBase(base, rule);
  const discountAmt = applied.discountAmt;
  const discountLabel = rule.label;

  const taxEnabled = clientPricing ? !!cp.india_tax_enabled : true;
  const rawClientTaxPct = cp.india_tax_percent;
  const clientTaxNum = Number(rawClientTaxPct);
  const gstPct = !taxEnabled
    ? 0
    : cp.india_tax_enabled
      ? rawClientTaxPct != null &&
          rawClientTaxPct !== '' &&
          Number.isFinite(clientTaxNum)
        ? Math.max(0, Math.min(100, clientTaxNum))
        : siteGstN
      : siteGstN;
  const taxLabel = String(cp.india_tax_label || 'GST').trim() || 'GST';

  let taxableBase = Math.max(0, base - discountAmt);

  const kind = String(cp.sacred_home_extra_discount_kind || '')
    .trim()
    .toLowerCase();
  let extraInr = 0;
  if (kind === 'percent' || kind === 'inr') {
    const rawV = cp.sacred_home_extra_discount_value;
    const val = Number(rawV);
    if (Number.isFinite(val) && val > 0) {
      const per = String(cp.sacred_home_extra_discount_per || 'cart')
        .trim()
        .toLowerCase();
      const n =
        per === 'participant' || per === 'per_participant'
          ? Math.max(1, Math.floor(Number.isFinite(mcRaw) ? mcRaw : 1))
          : 1;
      if (kind === 'inr') {
        extraInr = Math.min(taxableBase, val * n);
      } else {
        const pct = Math.max(0, Math.min(100, val));
        extraInr = (taxableBase * pct) / 100;
      }
    }
  }
  taxableBase = Math.max(0, taxableBase - extraInr);

  const gstAmount = (taxableBase * gstPct) / 100;
  const platformAmount = (taxableBase * platformPctN) / 100;
  const finalTotal = taxableBase + gstAmount + platformAmount;
  const roundedTotal = Math.round(finalTotal);

  return {
    discountKind: applied.discountKind,
    discountNominalPercent: applied.discountNominalPercent,
    discountPct: applied.discountPctEffective,
    discountLabel,
    discountAmt,
    taxableBase,
    gstPct,
    taxLabel,
    gstAmount,
    platformPct: platformPctN,
    platformAmount,
    finalTotal,
    roundedTotal,
  };
}

/**
 * `dashboard_mixed_total` on enrollments is the **taxable net** (after CRM India discount) so INR Stripe
 * stays aligned. Manual proof should show the same **final** rupees as Divine Cart Total (taxable + GST + platform).
 *
 * @param {number} taxableNet – enrollment `dashboard_mixed_total`
 * @param {object|null} clientPricing – `client_india_pricing` from GET /api/student/home
 * @param {object} settings – site settings (`india_gst_percent`, `india_platform_charge_percent`)
 */
export function computeIndiaManualProofPayableFromTaxableNet(taxableNet, clientPricing, settings) {
  const taxable = Math.max(0, Number(taxableNet) || 0);
  if (!Number.isFinite(taxable) || taxable <= 0) return null;

  const s = settings || {};
  const platformPctN = parseIndiaSitePercent(s, 'india_platform_charge_percent', 3);
  const siteGstN = parseIndiaSitePercent(s, 'india_gst_percent', 18);

  const cp = clientPricing || {};
  const taxEnabled = clientPricing ? !!cp.india_tax_enabled : true;
  const rawClientTaxPct = cp.india_tax_percent;
  const clientTaxNum = Number(rawClientTaxPct);
  const gstPct = !taxEnabled
    ? 0
    : cp.india_tax_enabled
      ? rawClientTaxPct != null &&
          rawClientTaxPct !== '' &&
          Number.isFinite(clientTaxNum)
        ? Math.max(0, Math.min(100, clientTaxNum))
        : siteGstN
      : siteGstN;
  const taxLabel = String(cp.india_tax_label || 'GST').trim() || 'GST';

  const gstAmount = (taxable * gstPct) / 100;
  const platformAmount = (taxable * platformPctN) / 100;
  const finalTotal = taxable + gstAmount + platformAmount;
  const roundedTotal = Math.round(finalTotal);

  return {
    taxableBase: taxable,
    gstPct,
    taxLabel,
    gstAmount,
    platformPct: platformPctN,
    platformAmount,
    finalTotal,
    roundedTotal,
  };
}
